import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { PenLine, Clock, Heart, MapPin, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORY_COLORS = {
  "Landmarks": "#2D6A4F",
  "Hidden Gems": "#B85C38",
  "Vanished Voices": "#5C4B8A",
  "Wild Echoes": "#1A5276",
  "City Pulses": "#8B5E3C",
};

export default function MyEchoes({ onOpen }) {
  const { user } = useAuth();
  const [stories, setStories] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, has_next: false, has_previous: false, total: 0 });
  const [loading, setLoading] = useState(true);

  const fetchMyStories = useCallback(async (page = 1) => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/stories?page=${page}&author_id=${user.id}`);
      setStories(res.data.stories);
      setPagination({
        page: res.data.page,
        total_pages: res.data.total_pages,
        has_next: res.data.has_next,
        has_previous: res.data.has_previous,
        total: res.data.total,
      });
    } catch { setStories([]); }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchMyStories(1);
  }, [fetchMyStories]);

  if (!user) return null;

  return (
    <div data-testid="my-echoes-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <PenLine size={20} className="text-[#B85C38]" />
          <h1 className="font-serif text-2xl sm:text-3xl font-medium text-[#1A1A1A]" data-testid="my-echoes-heading">
            My Echoes
          </h1>
          <span className="font-mono text-xs text-[#718096] uppercase tracking-wider">
            ({pagination.total})
          </span>
        </div>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="border border-[#E5E2D9] p-6 bg-white">
              <div className="skeleton h-5 w-24 mb-3" />
              <div className="skeleton h-6 w-3/4 mb-2" />
              <div className="skeleton h-4 w-full mb-2" />
              <div className="skeleton h-4 w-2/3" />
            </div>
          ))}
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-16 border border-[#E5E2D9] bg-white" data-testid="my-echoes-empty">
          <PenLine size={40} className="mx-auto text-[#E5E2D9] mb-4" />
          <h3 className="font-serif text-xl text-[#718096] mb-2">No Echoes Yet</h3>
          <p className="text-sm text-[#718096]">
            You haven't created any echoes yet. Click "New Echoe" to share your first story.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="my-echoes-list">
          {stories.map((story, i) => {
            const catColor = CATEGORY_COLORS[story.category] || "#718096";
            return (
              <div
                key={story.id}
                className={`bg-white border border-[#E5E2D9] p-6 transition-all hover:shadow-sm cursor-pointer animate-fade-in stagger-${(i % 8) + 1}`}
                onClick={() => onOpen?.(story)}
                data-testid={`my-echoe-${story.id}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span
                      className="type-badge"
                      style={{ borderColor: catColor, color: catColor }}
                    >
                      {story.category}
                    </span>
                    {story.na_id && (
                      <span className="type-badge">
                        <MapPin size={10} /> Sourced
                      </span>
                    )}
                    {story.media && story.media.length > 0 && (
                      <span className="type-badge">
                        {story.media.length} media
                      </span>
                    )}
                  </div>
                  <span className="font-mono text-xs text-[#718096]">
                    <Clock size={11} className="inline mr-1" />
                    {new Date(story.timestamp).toLocaleDateString()}
                  </span>
                </div>

                {/* Title & Content */}
                <h3 className="font-serif text-lg font-medium text-[#1A1A1A] mb-2 hover:text-[#B85C38] transition-colors">
                  {story.title}
                </h3>
                <p className="text-sm text-[#4A5568] leading-relaxed line-clamp-2 mb-3">
                  {story.content}
                </p>

                {/* Stats */}
                <div className="flex items-center gap-4 pt-3 border-t border-[#E5E2D9]">
                  <span className="flex items-center gap-1.5 text-xs text-[#718096]">
                    <Heart size={13} fill={story.favorites_count > 0 ? "#B85C38" : "none"} className={story.favorites_count > 0 ? "text-[#B85C38]" : ""} />
                    <span className="font-mono">{story.favorites_count} {story.favorites_count === 1 ? "favorite" : "favorites"}</span>
                  </span>
                  {story.location_coordinates && (() => {
                    const loc = story.location_coordinates;
                    const label = typeof loc === "string"
                      ? loc
                      : (loc.place_name || loc.address || (loc.lat != null && loc.lng != null ? `${loc.lat.toFixed(3)}, ${loc.lng.toFixed(3)}` : null));
                    if (!label) return null;
                    return (
                      <span className="flex items-center gap-1 text-xs text-[#718096]">
                        <MapPin size={11} />
                        <span className="font-mono">{label}</span>
                      </span>
                    );
                  })()}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E5E2D9]" data-testid="my-echoes-pagination">
          <button
            className="btn-secondary"
            disabled={!pagination.has_previous}
            onClick={() => fetchMyStories(pagination.page - 1)}
            style={{ opacity: pagination.has_previous ? 1 : 0.4 }}
          >
            <ChevronLeft size={14} /> Previous
          </button>
          <span className="font-mono text-xs text-[#718096]">
            Page {pagination.page} of {pagination.total_pages}
          </span>
          <button
            className="btn-secondary"
            disabled={!pagination.has_next}
            onClick={() => fetchMyStories(pagination.page + 1)}
            style={{ opacity: pagination.has_next ? 1 : 0.4 }}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
